import { useState } from 'react';
import type React from 'react';

import { cn } from '@change/ds-utils';

import { TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';

import { PulseDot } from './components/PulseDot';

/**
 * The whole bar is a link; the close button is a sibling, not a child, so it never navigates.
 * Dismissal plays the exit animation first, then calls `onDismissed` when it ends.
 */
export const SpikingPetitionBannerBar: React.FC<Props> = ({
	href,
	signatureCount,
	petitionTitle,
	timeRange,
	onClick,
	onClose,
	onDismissed,
}) => {
	const { translate, translatePlural, localizeNumber } = useI18n();
	const [leaving, setLeaving] = useState(false);

	const handleClose = () => {
		onClose();
		if (isMotionReduced()) {
			onDismissed();
		} else {
			setLeaving(true);
		}
	};

	const replacements = {
		count: (
			<strong className="font-semibold">
				{translatePlural('corgi.petition_details.spiking_petition_banner.people_count', signatureCount, {
					countLocalized: localizeNumber(signatureCount),
				})}
			</strong>
		),
		title: <strong className="font-semibold">{petitionTitle}</strong>,
		timeRange: (
			<span>{timeRange ?? translate('corgi.petition_details.spiking_petition_banner.default_time_range')}</span>
		),
		// Part of the message, not appended after it, so translators can place it.
		cta: (
			<span className="font-semibold underline underline-offset-[3px]">
				{translate('corgi.petition_details.spiking_petition_banner.cta')}
			</span>
		),
	};

	return (
		<div
			className={cn(
				'border-stroke-weaker bg-background-raised overflow-hidden border-b motion-reduce:animate-none',
				leaving ? 'animate-spike-banner-out' : 'animate-spike-banner-in',
			)}
			onAnimationEnd={leaving ? onDismissed : undefined}
			data-qa="spiking-petition-banner"
		>
			<div className="mx-auto flex max-w-[1200px] items-center">
				<a
					href={href}
					onClick={onClick}
					className="text-typography-strong hover:bg-fill-hover hover:text-typography-strong flex min-w-0 flex-1 items-center gap-2.5 py-3 pr-1 pl-4 no-underline md:pr-2 md:pl-6"
					data-qa="spiking-petition-banner-link"
				>
					<PulseDot />
					<span className="line-clamp-2 min-w-0 flex-1 text-sm leading-5 text-pretty md:line-clamp-1">
						<TranslateHtml value="corgi.petition_details.spiking_petition_banner.message" replacements={replacements} />
					</span>
				</a>
				<button
					type="button"
					onClick={handleClose}
					aria-label={translate('corgi.petition_details.spiking_petition_banner.dismiss_label')}
					className="text-icon-weak hover:bg-fill-hover mr-1 flex size-11 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-transparent md:mr-3"
					data-qa="spiking-petition-banner-close"
				>
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
						<path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
					</svg>
				</button>
			</div>
		</div>
	);
};

type Props = Readonly<{
	href: string;
	signatureCount: number;
	petitionTitle: string;
	// Replaces the default time range ("the past 24 hours") when set.
	timeRange: string | undefined;
	// Link clicked.
	onClick: () => void;
	// Close button clicked, before the exit animation starts.
	onClose: () => void;
	// The banner is finished leaving: after the exit animation, or at once under reduced motion.
	onDismissed: () => void;
}>;
